import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import FadeUp from "../components/FadeUp";
import { useLang, dictFull } from "../i18n";
import { api } from "../lib/api";
import { useEnquiry } from "../components/EnquiryContext";

export default function DressDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { openEnquiry } = useEnquiry();
  const [dress, setDress] = useState(null);
  const [active, setActive] = useState(0);
  const [related, setRelated] = useState([]);
  const touchStartX = useRef(null);

  const scrollTo = (i) => setActive(i);

  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    if (t) touchStartX.current = t.clientX;
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches?.[0];
    if (touchStartX.current == null || !t) return;
    const dx = t.clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 40) return;
    const n = dress?.images?.length || 0;
    setActive((a) => Math.min(n - 1, Math.max(0, a + (dx < 0 ? 1 : -1))));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setActive(0);
    api.get(`/dresses/${id}`, { params: { lang } }).then((r) => {
      setDress(r.data);
      document.title = `${r.data.name} — ${r.data.designer} | Per Lei Bridal`;
      r.data.images?.forEach((src) => { const img = new Image(); img.src = src; });
      return api.get("/dresses", { params: { designer: r.data.designer } });
    }).then((r) => r && setRelated(r.data.filter((d) => d.id !== id).slice(0, 3))).catch(() => {});
  }, [id, lang]);

  if (!dress) return <div className="pt-48 pb-32 text-center text-muted" data-testid="dress-loading">…</div>;

  const specs = [
    ...(dress.silhouette ? [[t("collection.silhouette"), dictFull.silhouettes[dress.silhouette]?.[lang] || dress.silhouette]] : []),
    ...(dress.fabric ? [[t("collection.fabric"), dictFull.fabrics[dress.fabric]?.[lang] || dress.fabric]] : []),
    [t("collection.leadTime"), dictFull.leadTimes[dress.lead_time]?.[lang] || dress.lead_time],
    [t("collection.sizes"), dress.sizes?.join(" · ")],
  ];

  return (
    <div data-testid="dress-detail-page">
      <div className="pt-32 lg:pt-40 max-w-[1440px] mx-auto px-6 lg:px-12">
        <Link to="/collezione" className="btn-line !text-[11px]" data-testid="dress-back">{t("collection.back")}</Link>
      </div>
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-12 lg:py-16 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-7 min-w-0" data-testid="dress-gallery">
          <div className="clip-frame relative overflow-hidden aspect-[3/4] touch-pan-y" data-testid="dress-gallery-strip"
            onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            <div className="flex h-full transition-transform duration-500 ease-[cubic-bezier(0.33,1,0.68,1)] will-change-transform"
              style={{ transform: `translateX(-${active * 100}%)` }}>
              {dress.images?.map((img, i) => (
                <img key={i} src={img} alt={`${dress.name} ${i + 1}`} draggable={false}
                  className="w-full h-full flex-shrink-0 object-cover spotlight-img"
                  loading={i === 0 ? "eager" : "lazy"}
                  onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ))}
            </div>
            {dress.images?.length > 1 && (
              <>
                <button data-testid="gallery-prev" onClick={() => scrollTo(Math.max(0, active - 1))}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-ivory/85 backdrop-blur-sm border border-softline text-charcoal hover:bg-gold transition-colors flex items-center justify-center"
                  style={{ display: active === 0 ? "none" : "flex" }}>‹</button>
                <button data-testid="gallery-next" onClick={() => scrollTo(Math.min(dress.images.length - 1, active + 1))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-ivory/85 backdrop-blur-sm border border-softline text-charcoal hover:bg-gold transition-colors flex items-center justify-center"
                  style={{ display: active === dress.images.length - 1 ? "none" : "flex" }}>›</button>
              </>
            )}
          </div>
          {dress.images?.length > 1 && (
            <div className="mt-4 flex gap-3 overflow-x-auto max-w-full pb-1" data-testid="dress-thumbs" style={{ scrollbarWidth: "thin" }}>
              {dress.images.map((img, i) => (
                <button key={i} onClick={() => scrollTo(i)} data-testid={`dress-thumb-${i}`}
                  className={`clip-frame w-16 flex-shrink-0 aspect-[3/4] border transition-[opacity,border-color] duration-300 ${active === i ? "border-gold" : "border-softline opacity-60 hover:opacity-100"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-4 lg:col-start-9 lg:sticky lg:top-28 self-start">
          <FadeUp>
            <p className="eyebrow">{dress.code}{dress.category === "bridal" && ` · ${t(`collections.${dress.collection}`)}`}</p>
            <h1 className="font-serif font-light text-5xl mt-4">{dress.name}</h1>
            <p className="mt-4 text-[15px] leading-relaxed text-stone">{dress.description}</p>
            <dl className="mt-10 space-y-0">
              {specs.filter(([, v]) => v).map(([k, v], i) => (
                <div key={i} className="flex gap-6 border-t border-softline py-4">
                  <dt className="eyebrow w-32 shrink-0 pt-1">{k}</dt>
                  <dd className="text-sm text-charcoal leading-relaxed">{v}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-10 flex flex-col gap-4">
              <button className="btn-solid" data-testid="dress-enquire-cta" onClick={() => openEnquiry(dress)}>{t("collection.enquire")}</button>
              <Link to="/appuntamenti" className="btn-ghost" data-testid="dress-book-cta" state={{ dressId: dress.id }}>{t("collection.book")}</Link>
            </div>
          </FadeUp>
        </div>
      </section>

      {related.length > 0 && (
        <section className="max-w-[1440px] mx-auto px-6 lg:px-12 pb-28" data-testid="dress-related">
          <h2 className="font-serif text-3xl mb-8">{lang === "it" ? "Dal medesimo designer" : lang === "en" ? "By the same designer" : "同品牌"}</h2>
          <div className="grid gap-6 sm:grid-cols-3">
            {related.map((d) => (
              <Link key={d.id} to={`/collezione/${d.id}`} data-testid={`related-${d.code}`} className="group block">
                <div className="clip-frame aspect-[3/4]"><img src={d.images?.[0]} alt={d.name} className="w-full h-full object-cover spotlight-img" loading="lazy" /></div>
                <p className="font-serif text-xl pt-4">{d.name}</p>
                <p className="text-[11px] tracking-[0.18em] uppercase text-muted">{d.designer}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
